import { ChangeDetectorRef, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { HttpService } from 'app/services/http.service';
import { Chart } from 'chart.js';

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss']
})
export class HomeComponent implements OnInit {

  usuario: any;

  @ViewChild('acumuladoCanvas') acumuladoCanvas: ElementRef;
  @ViewChild('movimientosCanvas') movimientosCanvas: ElementRef;

  chartAcumulado: any;
  chartMovimientos: any;
  loadingAvance: boolean = true;
  totalActual: number = 0;
  datos: any[] = [];
  datosFiltrados: any[] = [];
  anios: { label: string, value: string }[] = [];
  anioSeleccionado: string = 'TODOS';

  totalEmpresas: number = 0;
  totalEmpresasSinInstalaciones: number = 0;
  totalEmpresasConInstalaciones: number = 0;

  totalInstalaciones: number = 0;
  instalacionesEnPrueba: number = 0;
  instalacionesVencidas: number = 0;

  totalUsuarios: number = 0;
  totalSupervisores: number = 0;

  loading: boolean = true;

  constructor(
    private http: HttpService,
    private cdr: ChangeDetectorRef
  ) {
    this.usuario = JSON.parse(this.http.getLocalstorage('usuario'))
  }

  ngOnInit(): void {
    this.getDashboardStats();
    if (this.usuario.TipoUsuario != 'USER') {
      this.getAvances();
    }
  }

  async getDashboardStats(): Promise<void> {
    try {
      const stats = await this.http.getDashboardStatsService();
      if (stats) {
        this.totalEmpresas = stats.empresas.total;
        this.totalEmpresasConInstalaciones = stats.empresas.conInstalaciones;
        this.totalEmpresasSinInstalaciones = stats.empresas.sinInstalaciones;

        this.totalInstalaciones = stats.instalaciones.total;
        this.instalacionesEnPrueba = stats.instalaciones.enPrueba;
        this.instalacionesVencidas = stats.instalaciones.vencidas;

        this.totalUsuarios = stats.usuarios;
        this.totalSupervisores = stats.supervisores;

        this.cdr.detectChanges();
      }
    } catch (error) {
      console.error('Error al cargar estadísticas:', error);
    } finally {
      this.loading = false;
    }
  }

  async getAvances(): Promise<void> {
    try {
      this.loadingAvance = true;

      const response = await this.http.avanceInstalacionesService();
      this.datos = response.datos;
      this.totalActual = response.totalActual;

      // Extraer años únicos de los datos
      const aniosUnicos = [...new Set(this.datos.map(d => d.fecha.substring(0, 4)))].sort();
      this.anios = [
        { label: 'Todos', value: 'TODOS' },
        ...aniosUnicos.map(a => ({ label: a, value: a }))
      ];

      // Por defecto se muestra el año en curso (si hay datos); si no, todo el histórico
      const anioActual = new Date().getFullYear().toString();
      this.anioSeleccionado = aniosUnicos.indexOf(anioActual) !== -1 ? anioActual : 'TODOS';

      this.loadingAvance = false;
      this.cdr.detectChanges();

      this.filtrarYGraficar();
    } catch (error) {
      console.error('Error al obtener avance instalaciones:', error);
      this.loadingAvance = false;
      this.cdr.detectChanges();
    }
  }

  onAnioChange() {
    this.filtrarYGraficar();
  }

  filtrarYGraficar() {
    if (this.anioSeleccionado === 'TODOS') {
      this.datosFiltrados = this.datos;
    } else {
      this.datosFiltrados = this.datos.filter(d => d.fecha.startsWith(this.anioSeleccionado));
    }

    setTimeout(() => {
      if (this.chartAcumulado) this.chartAcumulado.destroy();
      if (this.chartMovimientos) this.chartMovimientos.destroy();

      this.graficarAcumulado();
      this.graficarMovimientos();
    })
  }

  graficarAcumulado() {
    const labels = this.datosFiltrados.map(e => e.fecha)
    const acumulado = this.datosFiltrados.map(e => e.acumulado)

    let titulo = this.anioSeleccionado === 'TODOS'
      ? 'Total actual: ' + this.totalActual + ' instalaciones'
      : 'Acumulado ' + this.anioSeleccionado;

    const datasets: any[] = [{
      label: 'Instalaciones acumuladas',
      data: acumulado,
      borderColor: '#3F51B5',
      backgroundColor: 'rgba(63, 81, 181, 0.1)',
      borderWidth: 2,
      fill: true
    }];

    const proyeccion = this.calcularProyeccion();
    if (proyeccion) {
      labels.push(...proyeccion.labels);
      datasets.push({
        label: 'Proyección',
        data: proyeccion.data,
        borderColor: '#FF9800',
        backgroundColor: 'rgba(255, 152, 0, 0.08)',
        borderWidth: 2,
        borderDash: [6, 4],
        pointRadius: 3,
        fill: true
      });
      titulo += ' (proyección a diciembre: ' + proyeccion.totalProyectado + ')';
    }

    this.chartAcumulado = new Chart(this.acumuladoCanvas.nativeElement, {
      type: 'line',
      data: {
        labels,
        datasets
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        title: {
          display: true,
          text: titulo,
          fontSize: 14
        },
        scales: {
          yAxes: [{
            scaleLabel: { display: true, labelString: 'Instalaciones' },
            ticks: { beginAtZero: this.anioSeleccionado !== 'TODOS' }
          }]
        }
      }
    })
  }

  /**
   * Proyecta los meses faltantes del año en curso mediante una regresión lineal
   * simple sobre el acumulado de los meses ya registrados.
   * Devuelve null si no corresponde proyectar (año pasado, "Todos" o año completo).
   */
  calcularProyeccion(): { labels: string[], data: any[], totalProyectado: number } {
    if (this.anioSeleccionado === 'TODOS') return null;

    const anioActual = new Date().getFullYear().toString();
    if (this.anioSeleccionado !== anioActual) return null;

    const n = this.datosFiltrados.length;
    if (n < 2 || n >= 12) return null;

    // Regresión lineal (mínimos cuadrados) sobre el acumulado: y = m*x + b
    const acumulado = this.datosFiltrados.map(e => e.acumulado);
    const sumX = (n - 1) * n / 2;
    const sumY = acumulado.reduce((a, b) => a + b, 0);
    const sumXY = acumulado.reduce((acc, y, x) => acc + x * y, 0);
    const sumX2 = acumulado.reduce((acc, _, x) => acc + x * x, 0);

    const denominador = n * sumX2 - sumX * sumX;
    if (denominador === 0) return null;

    const m = (n * sumXY - sumX * sumY) / denominador;
    const b = (sumY - m * sumX) / n;

    const labels: string[] = [];
    // La serie proyectada arranca en el último mes real para enlazar ambas líneas
    const data: any[] = new Array(n - 1).fill(null);
    data.push(acumulado[n - 1]);

    const ultimoMes = parseInt(this.datosFiltrados[n - 1].fecha.substring(5, 7), 10);
    if (ultimoMes >= 12) return null;

    let anterior = acumulado[n - 1];
    for (let mesNum = ultimoMes + 1, x = n; mesNum <= 12; mesNum++, x++) {
      const mes = ('0' + mesNum).slice(-2);
      labels.push(this.anioSeleccionado + '-' + mes);
      // El acumulado nunca decrece: si la tendencia es negativa se mantiene plano
      anterior = Math.max(anterior, Math.round(m * x + b));
      data.push(anterior);
    }

    return { labels, data, totalProyectado: anterior };
  }

  graficarMovimientos() {
    const labels = this.datosFiltrados.map(e => e.fecha)
    const creadas = this.datosFiltrados.map(e => e.creadas)
    const eliminadas = this.datosFiltrados.map(e => e.eliminadas)

    const neto = this.datosFiltrados.map(e => e.creadas - e.eliminadas)

    const totalCreadas = creadas.reduce((a, b) => a + b, 0);
    const totalEliminadas = eliminadas.reduce((a, b) => a + b, 0);
    const totalNeto = totalCreadas - totalEliminadas;

    const titulo = this.anioSeleccionado === 'TODOS'
      ? 'Movimientos mensuales'
      : 'Movimientos ' + this.anioSeleccionado;

    this.chartMovimientos = new Chart(this.movimientosCanvas.nativeElement, {
      type: 'bar',
      data: {
        labels,
        datasets: [
          {
            label: 'Creadas (' + totalCreadas + ')',
            data: creadas,
            backgroundColor: 'rgba(76, 175, 80, 0.7)',
            borderColor: '#4CAF50',
            borderWidth: 1
          },
          {
            label: 'Eliminadas (' + totalEliminadas + ')',
            data: eliminadas,
            backgroundColor: 'rgba(244, 67, 54, 0.7)',
            borderColor: '#F44336',
            borderWidth: 1
          },
          {
            label: 'Neto (' + (totalNeto > 0 ? '+' : '') + totalNeto + ')',
            data: neto,
            backgroundColor: 'rgba(63, 81, 181, 0.7)',
            borderColor: '#3F51B5',
            borderWidth: 1
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        title: {
          display: true,
          text: titulo,
          fontSize: 14
        },
        scales: {
          yAxes: [{
            scaleLabel: { display: true, labelString: 'Cantidad' },
            ticks: { beginAtZero: true }
          }]
        }
      }
    })
  }

}
